import React, {ReactNode} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {ContentHotel} from '@findhotel/sapi'
import {imageProvider} from 'utils/imageProvider'
import {getNumberOfNights} from 'utils/offers'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {Button} from '@vio/atlas/Button'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon, IconName} from '@vio/atlas/Icon'
import {Image} from '@vio/atlas/Image'
import {BottomBar} from '@vio/atlas/src/components/designSystem/helpers/BottomBar'
import mirror from '@vio/atlas/src/utils/mirror'
import {StarRating} from '@vio/atlas/StarRating'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {
  getCancellationDeadline,
  getRefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import {
  BovioPaymentMode,
  getOfferPaymentMode
} from '@vio/core/src/offer/business/offerPaymentMode'
import {Offer} from '@vio/core/src/offer/types/offer'
import {StayPrices} from '@vio/core/src/price/business/roomOfferPrices'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'
import {dateStringToMiddayDate} from '@vio/core/src/utils/date'
import {getRoundingStrategy} from '@vio/core/src/utils/number'
import {OfferPerks} from '@vio/shared/src/accommodation/OfferPerks/OfferPerks'
import {datePickerMessages} from '@vio/shared/src/search/DatePicker/helpers/datePickerMessages'
import {guestPickerMessages} from '@vio/shared/src/search/GuestPicker/guestPickerMessages'
import {GuestSplitText} from '@vio/shared/src/search/GuestPicker/GuestSplitText'
import {Price} from '@vio/shared/src/search/Price'

const messages = defineMessages({
  title: {
    id: 'bookOnAppConfirmation.title',
    defaultMessage: 'Available on Vio app'
  },
  subtitle: {
    id: 'bookOnAppConfirmation.subtitle',
    defaultMessage: 'You’ll complete your booking on the Vio app'
  },
  cta: {
    id: 'bookOnAppConfirmation.cta',
    defaultMessage: 'Continue on app'
  },
  yourSelection: {
    id: 'yourSelection',
    defaultMessage: 'Your selection'
  },
  nightlyPrice: {
    id: 'nightlyPrice',
    defaultMessage: 'Nightly price'
  },
  total: {
    id: 'payment.total',
    defaultMessage: 'Total'
  },
  withTaxesAndFees: {
    id: 'taxation.withTaxesAndFees',
    defaultMessage: 'with taxes & fees'
  }
})

const DealCard = styled.div`
  width: 100%;
  border: 1px solid ${cssTheme.colors.border.neutral.secondary};
  border-radius: ${cssTheme.layout.radius.lg};
  overflow: hidden;
`

const HotelImage = styled.div`
  width: 74px;
  height: 76px;
  flex-shrink: 0;
  border-radius: ${cssTheme.layout.radius.md};
  overflow: hidden;
`

const Stars = styled.div`
  display: inline-flex;
  vertical-align: middle;
  margin-inline-start: ${cssTheme.layout.spacing.s250};
`

// The sheet's design gives the cancellation row a 22px minimum height; the
// room card keeps the shared perks as they are
const Perks = styled.div`
  [data-id='cancellation-label'] {
    min-height: 22px;
  }
`

const ChevronWrapper = styled.div`
  ${mirror()}
`

const Title = () => (
  <Box display="flex" alignItems="center" gap="s300">
    <Icon name="CheckCircle" colorPath="content.success.secondary" />
    <Text variant="titleM">
      <FormattedMessageWrapper {...messages.title} />
    </Text>
  </Box>
)

const IconRow = ({icon, children}: {icon: IconName; children: ReactNode}) => (
  <Box display="flex" alignItems="center" gap="s250">
    <Icon name={icon} size="sm" colorPath="content.neutral.primary" />
    <Text variant="bodyS">{children}</Text>
  </Box>
)

type Props = {
  isOpen: boolean
  hotel: ContentHotel
  roomName: string
  offer: Offer
  /** Nightly and total price from the offer check, with the AP anchor */
  prices: StayPrices
  paymentMode?: BovioPaymentMode
  checkIn: string
  checkOut: string
  rooms: string
  hotelTimeZone?: string
  onClose: () => void
  onContinue: () => void
}

export const BookOnAppConfirmationSheet = ({
  isOpen,
  hotel,
  roomName,
  offer,
  prices,
  paymentMode,
  checkIn,
  checkOut,
  rooms,
  hotelTimeZone,
  onClose,
  onContinue
}: Props) => {
  const {formatDate} = useIntl()
  const formatStayDate = (date: string) =>
    formatDate(dateStringToMiddayDate(date), {month: 'short', day: '2-digit'})
  const cancellationPenalties = offer.cancellationPenalties || []
  const roomsConfiguration = roomsToConfigurationObject(rooms)

  return (
    <BottomSheet
      isOpen={isOpen}
      navBarProps={{centerContent: <Title />}}
      onClose={onClose}
      snapPoints={{top: 'nearFull', middle: 0.95}}
      noPaddings
      footerContent={
        <BottomBar>
          <Button
            variant="primary"
            fullWidth
            onClick={onContinue}
            iconEnd={
              <ChevronWrapper>
                <Icon name="ChevronRight" />
              </ChevronWrapper>
            }
            dataId="bookOnAppConfirmationCTA"
          >
            <FormattedMessageWrapper {...messages.cta} />
          </Button>
        </BottomBar>
      }
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="s400"
        paddingTop="s500"
        paddingLeft="s500"
        paddingRight="s500"
      >
        <Text variant="bodyM" align="center">
          <FormattedMessageWrapper {...messages.subtitle} />
        </Text>
        <DealCard>
          <Box display="flex" alignItems="flex-start" gap="s400" padding="s500">
            {hotel.imageURIs?.[0] && (
              <HotelImage>
                <Image
                  url={hotel.imageURIs[0]}
                  imageProvider={imageProvider}
                  isResponsive
                />
              </HotelImage>
            )}
            <Box display="flex" flexDirection="column" gap="s250">
              <Text variant="titleM" tag="div">
                {hotel.hotelName}
                {hotel.starRating > 0 && (
                  <Stars>
                    <StarRating size="xs" rating={hotel.starRating} />
                  </Stars>
                )}
              </Text>
              <Box display="flex" flexDirection="column" gap="s100">
                <IconRow icon="Calendar">
                  {`${formatStayDate(checkIn)} - ${formatStayDate(checkOut)}`}
                </IconRow>
                <IconRow icon="User">
                  <FormattedMessageWrapper
                    {...guestPickerMessages.room}
                    values={{count: roomsConfiguration.numberOfRooms}}
                  />
                  {', '}
                  <FormattedMessageWrapper
                    {...datePickerMessages.nights}
                    values={{count: getNumberOfNights(checkIn, checkOut)}}
                  />
                  {' · '}
                  <GuestSplitText
                    numberOfAdults={roomsConfiguration.numberOfAdults}
                    numberOfChildren={roomsConfiguration.numberOfChildren}
                  />
                </IconRow>
              </Box>
            </Box>
          </Box>
          <Divider />
          <Box display="flex" flexDirection="column" gap="s500" padding="s500">
            <Box display="flex" flexDirection="column" gap="s100">
              <Text variant="titleM">
                <FormattedMessageWrapper {...messages.yourSelection} />
              </Text>
              <Box display="flex" flexDirection="column" gap="s300">
                <Text variant="bodyM">{roomName}</Text>
                <Perks>
                  <OfferPerks
                    paymentType={getOfferPaymentMode({offer, paymentMode})}
                    refundType={getRefundType(cancellationPenalties)}
                    cancellationDeadline={
                      getCancellationDeadline(cancellationPenalties) ?? ''
                    }
                    mealPlan={offer.mealPlan}
                    timezone={hotelTimeZone}
                  />
                </Perks>
              </Box>
            </Box>
            <Divider />
            <Box display="flex" flexDirection="column" gap="s300">
              <Box
                display="flex"
                alignItems="baseline"
                justifyContent="space-between"
              >
                <Text variant="bodyM">
                  <FormattedMessageWrapper {...messages.nightlyPrice} />
                </Text>
                <Box display="flex" alignItems="baseline" columnGap="s200">
                  {prices.nightly.showAnchor && (
                    <Price
                      isAnchor
                      currency={prices.currencyCode}
                      value={prices.nightly.anchor}
                      roundingStrategy={getRoundingStrategy()}
                    />
                  )}
                  <Price
                    isPrivate
                    currency={prices.currencyCode}
                    value={prices.nightly.price}
                    roundingStrategy={getRoundingStrategy()}
                  />
                </Box>
              </Box>
              <Box display="flex" flexDirection="column">
                <Box
                  display="flex"
                  alignItems="baseline"
                  justifyContent="space-between"
                >
                  <Text variant="titleM">
                    <FormattedMessageWrapper {...messages.total} />
                  </Text>
                  <Price
                    size="md"
                    currency={prices.currencyCode}
                    value={prices.total.price}
                    roundingStrategy={getRoundingStrategy()}
                    dataId="bookOnAppConfirmationTotal"
                  />
                </Box>
                <Text
                  variant="bodyS"
                  colorPath="content.neutral.secondary"
                  align="right"
                >
                  <FormattedMessageWrapper {...messages.withTaxesAndFees} />
                </Text>
              </Box>
            </Box>
          </Box>
        </DealCard>
      </Box>
    </BottomSheet>
  )
}
